import type { Contact } from '@/api/types';

/**
 * Client-side vCard 3.0 export.
 *
 * **Export only.** Import is deliberately absent: parsing vCards from four
 * different address-book products is fiddly, lossy when done twice, and the
 * backend already speaks CardDAV — doc 06 §8 puts import behind
 * `POST /address-books/{id}/import`, which does not exist yet, so the UI
 * offers it as a disabled control with the reason on it rather than a
 * half-parser that silently drops the fields it does not know.
 *
 * Export is safe to do here because it only writes what the client already
 * holds. It is 3.0 for the same reason the backend creates 3.0 (doc 05 §2.2):
 * it is what Apple Contacts, Android, Thunderbird and Outlook all import
 * without argument.
 *
 * **The app's Export does not use this.** It downloads the server's
 * `export.vcf` (#343), which is the stored cards verbatim — every page of the
 * book, photos and other clients' `X-` properties included. This serializer
 * writes only what `Contact` models and has no photo bytes, so it is what the
 * mock API answers `exportVcf` with, and nothing a user downloads.
 */

/** Escapes a vCard text value: RFC 6350 §3.4 (`\`, `,`, `;`, newline). */
function escapeValue(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

/** Folds a content line at 75 octets, as every consuming client expects. */
function fold(line: string): string {
  if (line.length <= 75) return line;
  const parts: string[] = [line.slice(0, 75)];
  for (let index = 75; index < line.length; index += 74) {
    parts.push(` ${line.slice(index, index + 74)}`);
  }
  return parts.join('\r\n');
}

function property(name: string, value: string, parameters?: string): string | undefined {
  if (value.trim() === '') return undefined;
  const head = parameters ? `${name};${parameters}` : name;
  return fold(`${head}:${value}`);
}

/** One contact as a vCard 3.0 card. */
export function toVCard(contact: Contact): string {
  const lines: (string | undefined)[] = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    property('FN', escapeValue(contact.displayName)),
    property(
      'N',
      `${escapeValue(contact.lastName ?? '')};${escapeValue(contact.firstName ?? '')};;;`,
    ),
    contact.organization ? property('ORG', escapeValue(contact.organization)) : undefined,
    contact.title ? property('TITLE', escapeValue(contact.title)) : undefined,
    ...contact.emails.map((email) =>
      property(
        'EMAIL',
        escapeValue(email.value),
        `TYPE=${vcardType(email.type)}${email.primary ? ',PREF' : ''}`,
      ),
    ),
    ...contact.phones.map((phone) =>
      property(
        'TEL',
        escapeValue(phone.value),
        `TYPE=${vcardType(phone.type)}${phone.primary ? ',PREF' : ''}`,
      ),
    ),
    ...contact.addresses.map((address) =>
      property(
        'ADR',
        [
          '',
          '',
          escapeValue(address.street ?? ''),
          escapeValue(address.city ?? ''),
          escapeValue(address.region ?? ''),
          escapeValue(address.postalCode ?? ''),
          escapeValue(address.country ?? ''),
        ].join(';'),
        `TYPE=${vcardType(address.type)}`,
      ),
    ),
    contact.nickname ? property('NICKNAME', escapeValue(contact.nickname)) : undefined,
    ...(contact.urls ?? []).map((url) =>
      property('URL', url.value, url.type ? `TYPE=${vcardType(url.type)}` : undefined),
    ),
    contact.birthday ? property('BDAY', contact.birthday) : undefined,
    contact.anniversary ? property('ANNIVERSARY', contact.anniversary) : undefined,
    contact.categories.length > 0
      ? property('CATEGORIES', contact.categories.map(escapeValue).join(','))
      : undefined,
    contact.notes ? property('NOTE', escapeValue(contact.notes)) : undefined,
    property('REV', contact.updatedAt),
    'END:VCARD',
  ];
  return lines.filter((line): line is string => line !== undefined).join('\r\n');
}

/** A whole selection as one `.vcf` file, which is how every client reads them. */
export function toVCardFile(contacts: readonly Contact[]): string {
  return `${contacts.map(toVCard).join('\r\n')}\r\n`;
}

function vcardType(type: string): string {
  const normalized = type.trim().toUpperCase();
  return normalized === '' ? 'OTHER' : normalized;
}

/** A filename that survives a file picker: no separators, no surprises. */
export function vcardFilename(label: string): string {
  const safe = label.trim().replace(/[^A-Za-z0-9 _-]+/g, '').replace(/\s+/g, '-');
  return `${safe === '' ? 'contacts' : safe}.vcf`;
}

/**
 * Hands the text to the browser as a download.
 *
 * Separated from the serializer so the serializer stays testable without a DOM
 * and without a stubbed `URL.createObjectURL`.
 */
export function downloadVCard(text: string, filename: string): void {
  const blob = new Blob([text], { type: 'text/vcard;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next turn: revoking synchronously races the download in
  // some engines.
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}
